import {
  ICart,
  IField,
  IFormDocument,
  ITotalSum,
} from 'app/services/api5-service/api.interface';
import { map } from 'rxjs/operators';

import {
  TotalSumTax,
  TotalSumPromocode,
  TotalSumDiscount,
  TotalSumUi,
} from 'common/total-sum/model';
import { DocHelper } from 'utils/dochelper';

import { TotalSumProps } from './model/total-sum-props.interface';

import { ISPFormState, ISPFieldConfig, ISPFieldType } from '../../model';

/**
 * Get taxes information from BE DTO for displaying
 *
 * @param cartData
 * @param doc
 */
function getTotalSumTaxes(cartData: ICart, doc: IFormDocument): TotalSumTax[] {
  const taxes = DocHelper.getArrayWrapperValue(cartData.tax_amount);
  const taxesElements = taxes.map<TotalSumTax>(tax => ({
    name: DocHelper.getMessage('cart_total_tax_amount', doc),
    value: tax.$,
  }));

  const comissions = DocHelper.getArrayWrapperValue(cartData.comission_amount);
  const comissionsElements = comissions.map<TotalSumTax>(tax => ({
    name: DocHelper.getMessage('cart_total_comission_amount', doc),
    value: tax.$,
  }));

  return taxesElements.concat(comissionsElements);
}

/**
 * Get promocodes information from BE DTO for displaying
 *
 * @param cartData
 */
function getTotalSumPromocodes(cartData: ICart): TotalSumPromocode[] {
  if (!cartData.promocode) {
    return [];
  }

  const promocodes = DocHelper.getArrayWrapperValue(cartData.promocode);
  return promocodes.map<TotalSumPromocode>(promocode => ({
    name: promocode.$name,
    value: promocode.$,
  }));
}

/**
 * Get discount information from BE DTO for displaying
 *
 * @param cartData
 * @param doc
 */
function getTotalSumDiscounts(
  cartData: ICart,
  doc: IFormDocument,
): TotalSumDiscount[] {
  if (!cartData.discount) {
    return [];
  }

  const discounts = DocHelper.getArrayWrapperValue(cartData.discount);
  return discounts.map<TotalSumDiscount>(discount => ({
    name: DocHelper.getMessage('cart_total_discount', doc),
    value: discount.$,
  }));
}

/**
 * Get total sum data
 *
 * @param control - control (subfield) metadata
 * @param doc
 */
function getTotalSum(
  control: ITotalSum,
  doc: IFormDocument,
): TotalSumUi | undefined {
  const cartData = DocHelper.getTotalSumData(control, doc);
  if (!cartData) {
    return;
  }

  const currency = cartData.currency.$;
  const total = cartData.cost.$;
  const totalWithoutDiscount = cartData.base_cost.$;

  let totalCostForPaymethod;
  if (cartData.paymethod_cost && cartData.paymethod_currency) {
    totalCostForPaymethod = {
      totalCost: cartData.paymethod_cost.$,
      totalCurrency: cartData.paymethod_currency.$,
    };
  }

  return {
    currency,
    totalCost: {
      total,
      totalWithoutDiscount,
    },
    totalCostForPaymethod: totalCostForPaymethod,
    discounts: getTotalSumDiscounts(cartData, doc),
    taxes: getTotalSumTaxes(cartData, doc),
    promocodes: getTotalSumPromocodes(cartData),
    disabled: !Number(cartData.count.$),
    messages: {
      totalPosition: DocHelper.getMessage('cart_total_base_cost', doc),
      titleDiscount: DocHelper.getMessage('cart_total_discount_title', doc),
      total: DocHelper.getMessage('cart_total_cost', doc),
      totalPaymethodCost: DocHelper.getMessage(
        'cart_total_paymethod_cost',
        doc,
      ),
      disabled: DocHelper.getMessage('cart_total_disable', doc),
    },
  };
}

/**
 * Get config for cart field from doc
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - state
 */
export function getTotalSumProps(
  control: ITotalSum,
  field: IField,
  state: ISPFormState,
): TotalSumProps {
  return {
    originalControl: control,
    originalField: field,
    data$: state.docSubject.pipe(map(doc => getTotalSum(control, doc))),
  };
}

/**
 * Get config for cart field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getTotalSumConfig(
  control: ITotalSum,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.TotalSum> {
  const props = getTotalSumProps(control, field, state);

  const config: ISPFieldConfig<ISPFieldType.TotalSum> = {
    type: ISPFieldType.TotalSum,
    wrappers: [],
    props,
  };

  return config;
}
